import { accountNumberConfig } from 'configureBrand'
import { format } from 'date-fns'
import formatCurrency from 'syf-js-utilities/formats/formatCurrency'
import { AnyObject, InputEvent, UnknownKeys } from 'typings/custom'

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2
})

export const negativeFormatAmount = (amount: number): string => {
  if (amount === undefined) return ''
  return formatter.format(-1 * amount || 0)
}

export const formatDate = (
  dateValue?: string | Date,
  dateformat = 'dd/MM/yyyy'
): string => {
  const dateObject = dateValue ? new Date(dateValue) : new Date()
  return format(dateObject, dateformat)
}

/**
 * Replaces characters of a string with a given mask except the last 4 characters.
 *
 * @param value value to be masked
 * @param maskChar character to use for masking. '•' used for default.
 * @param maskCount Number of maskChar to use as a replacement for each character. default is 1
 * @returns string
 *
 * @example
 * // returns '••••1714'
 * maskWithLast4Digits('9900041714', '•', 4)
 * @example
 * // returns  '(•••) •••-7890'
 * maskWithLast4Digits('1234567890', '(•••) •••-')
 */
export const maskWithLast4Digits = (
  value?: string,
  maskChar = '•',
  maskCount = 1
): string => {
  if (value === undefined) return ''
  return `${maskChar.repeat(maskCount)}${value.slice(
    value.length - 4,
    value.length
  )}`
}

export const mask = (length: number): string => '•'.repeat(length)

export const maskSubstr = (
  string: string,
  from = 0,
  to = string.length
): string => {
  if (from > string.length || to < from) {
    return string
  }
  return string.substring(0, from) + mask(to - from) + string.substring(to)
}

// Formats '23 Walker' to '23•••••••'
export const maskAddress = (address: string): string => {
  return address
    .trim()
    .split(/(\s+)/)
    .map((value, index) => {
      if (index === 0) return maskSubstr(value, 2)
      return maskSubstr(value)
    })
    .join(' ')
    .replace(/\s/g, '')
}

// Formats 'sample@domain.com' to 's••••e@domain.com'
export const maskEmail = (email: string): string => {
  if (email === '' || !email.includes('@')) {
    return email
  }
  const [username, domain] = email.split('@')
  return [maskSubstr(username, 1, username.length - 1), domain].join('@')
}

// Masks an accountNumber 82130108799187 to 8•••••••••9187
export const maskAccountNumber = (accountNumber: string): string => {
  // number of characters which should not be masked at the end of the number
  const unmaskAtEndSize = 4
  const endMaskIdx = accountNumber.length - unmaskAtEndSize
  const startMaskIdx = 1

  const maskSize = endMaskIdx - startMaskIdx

  if (maskSize < 1) {
    // account number is too short to mask
    return accountNumber
  }

  return maskSubstr(accountNumber, startMaskIdx, endMaskIdx)
}

export const onlyDigits = /[^\d]/g

export const getOnlyNumericDigits = (value: string): string =>
  value.replace(onlyDigits, '')

export const maskPhoneNumber = (value: string): string => {
  if (!value) {
    return value
  }

  const onlyNums = getOnlyNumericDigits(value)
  const { length: numsCount } = onlyNums

  if (numsCount === 0) {
    return ''
  }

  if (numsCount > 0 && numsCount <= 2) {
    return `(${mask(numsCount)}`
  }

  if (numsCount === 3) {
    return `(${mask(3)})`
  }

  if (numsCount >= 4 && numsCount <= 6) {
    return `(${mask(3)}) ${mask(numsCount - 3)}`
  }

  return `(${mask(3)}) ${mask(3)}-${onlyNums.slice(6)}`
}

export const normalizeCreditAmount = (value: string): string => {
  return `$${value}`
}

export const maskFieldValue = (value: AnyObject): string => {
  const [fieldName] = Object.keys(value)
  const fieldValue = value[fieldName]

  if (value[fieldName] !== '') {
    switch (fieldName) {
      case 'email':
        return maskEmail(fieldValue)
      case 'streetAddress':
      case 'streetAddressNextLine':
        return maskAddress(fieldValue)
      case 'city':
        return maskSubstr(fieldValue, 2)
      case 'stateName':
        return maskSubstr(fieldValue, 1)
      case 'ZIPCode':
        return maskSubstr(fieldValue, 0, fieldValue.length - 2)
      case 'faxNumber':
      case 'phoneNumber':
        return maskPhoneNumber(fieldValue)
      case 'creditAmount':
        return normalizeCreditAmount(fieldValue)
      default:
    }
  }
  return ''
}

export const maskFormInfo = (formInfo: AnyObject): AnyObject =>
  Object.keys(formInfo)
    .map(maskName => {
      const unMaskValue = formInfo[maskName] || ''
      const maskedValue = maskFieldValue({ [maskName]: unMaskValue })
      const unMaskName = `unMask${maskName
        .slice(0, 1)
        .toUpperCase()}${maskName.slice(1)}`

      return { [maskName]: maskedValue, [unMaskName]: unMaskValue }
    })
    .reduce((obj, item) => ({ ...obj, ...item }), {})

export const removeWhitespace = (value: string) => {
  return value.replace(/\s/g, '')
}

export const normalizeEmail = (value: string): string => {
  if (!value) {
    return value
  }

  const words = value.split('@')

  if (words.length > 2) return value

  const [emailId, domain = ''] = words
  const charCount = emailId.length

  if (charCount <= 2 && words.length <= 1)
    return `${mask(emailId.slice(0, charCount).length)}`

  if (charCount <= 2)
    return `${mask(emailId.slice(0, charCount).length)}@${domain}`

  if (charCount >= 10)
    return `${emailId.slice(0, 1)}${mask(8)}${emailId.slice(
      charCount - 1,
      emailId.length
    )}@${domain}`

  return `${emailId.slice(0, 1)}${mask(
    emailId.slice(1, charCount - 1).length
  )}${emailId.slice(charCount - 1, emailId.length)}@${domain}`
}

export const formatPhoneNumber = (value: string): string => {
  if (value) {
    const trimmedValue = `${value}`.replace(/\D/g, '')
    const match = trimmedValue.match(/^(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`
    }
  }
  return value
}

export const formatTime = (timeLeft = 0): string => {
  const minutes = `0${Math.floor(timeLeft / 60)}`
  const secondsCalc = timeLeft % 60
  const seconds = secondsCalc > 9 ? secondsCalc : `0${secondsCalc}`
  return `${minutes}:${seconds}`
}

/**
 * Applies optional custom formating for specific brand/client if client
 * configuration has accountNumberConfig.getCustomFormat defined.
 * @param accountNumber raw account number string to format
 * @returns formatted account number string
 */
export const formatAccountNumber = (accountNumber: string): string => {
  // use custom formatter if it exists
  return accountNumberConfig.getCustomFormat?.(accountNumber) || accountNumber
}

/**
 * Formats 8-digit numeric string to MM/dd/yyyy date string format.
 * ex) '12122020' returns '12/12/2020'
 *
 */
export const stringToDateFormat = (date: string): string => {
  const { length: noOfCharacters } = date
  if (noOfCharacters !== 8) return ''

  return `${date.slice(0, 2)}/${date.slice(2, 4)}/${date.slice(4, 8)}`
}

export const monthDayFormatted = (monthDay: number): string => {
  return monthDay.toString().replace(/(^|\D)(\d)(?!\d)/g, '$10$2')
}

export const formatStringWithoutSpaces = (name = ''): string =>
  name?.replace(/\s/g, '')

export const titleCase = (fieldValue: string): string => {
  return fieldValue.replace(/^_*(.)|_+(.)/g, (s, c, d) =>
    c ? c.toUpperCase() : ` ${d.toUpperCase()}`
  )
}

export const formatFieldName = (name: string): string =>
  `${name.slice(0, 1).toUpperCase()}${name.slice(1)}`

export const swapMaskValue = ({
  target: {
    name,
    value,
    dataset: { value: unMaskValue }
  }
}: InputEvent): UnknownKeys => {
  let formattedValue = value
  let formattedMaskedValue = unMaskValue
  const trimSpacesField = ['email']
  if (trimSpacesField.includes(name)) {
    formattedValue = value?.replace(/ /g, '')
    formattedMaskedValue = unMaskValue?.replace(/ /g, '')
  }
  const unMaskName = `unMask${formatFieldName(name)}`

  return {
    [name]: formattedMaskedValue,
    [unMaskName]: formattedValue
  }
}

export const maskInputValue = ({
  target: { name, value }
}: InputEvent): UnknownKeys => {
  const unMaskName = `unMask${formatFieldName(name)}`
  return {
    [name]: value,
    [unMaskName]: maskFieldValue({ [name]: value })
  }
}

/*
 * Support: Safari and IE Dates
 * Description: Invalid date issue when date string has hyphen as seperator (ex. 22-10-2021)
 * Fix: Replaced the hyphen with slash (ex. 22/10/2021)
 */
export const formatHyphenDate = (date: string): string => {
  const formattedDate = date.substring(0, date.indexOf('.'))
  return formattedDate.replace(/-/g, '/')
}

/**
 * Wrapper for syf-js-utils formatCurrency so that any values which format to
 * $NaN will be replaced by emptyValue.
 *
 * If string converts to NaN, returns emptyValue.
 * @param amount The amount string to format.
 * @param emptyValue string to return if amount is a falsy value
 * @returns formatted string or emptyValue string
 */
export const safelyFormatCurrency = (amount: string, emptyValue = '') => {
  const formatted = formatCurrency(amount)
  return /nan/i.test(formatted) ? emptyValue : formatted
}
